//@flow
import {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import type {RootState} from '../..';
import {useIsFeatureInTier} from '../../../utils/component';
import {useActions} from '../../../utils/globalStore';
import {getImageUrl} from '../../../utils/image_provider';
import {actions as ProductsActions} from '../duck';
import {
  searchProductsMinimalService,
  getProductsMinimalService,
  getProductMetadataSettingService,
  editProductService,
  listProductsService,
  setProductTaxService,
} from '../promises';

import type {ListProductsParams} from '../promises';

import type {
  MinimalProduct,
  ProductFilterParams,
  ProductMetaDataSetting,
  Product,
} from '../../../entities';

import type {ProductMetadataSettingRequest} from '../api';

const isCreatingCatalogueSelector = (state: RootState) =>
  state.products.isCreatingCatalogue;

const catalogueSelector = (state: RootState) => state.products.catalogue;

const createCatalogueSuccessSelector = (state: RootState) =>
  state.products.createCatalogueSuccess;

type Params = {
  onError?: Function,
  onSuccess?: Function,
};

const LIMIT_TRIES = 10;
const INTERVAL = 18000;

export function useGenerateCataloguePDF({onError, onSuccess}: Params = {}) {
  const {createProductsCatalogue, fetchProductsCatalogue} = useActions(
    ProductsActions
  );
  const isCreatingCatalogue = useSelector(isCreatingCatalogueSelector);
  const createCatalogueSuccess = useSelector(createCatalogueSuccessSelector);
  const catalogue = useSelector(catalogueSelector);

  const [isFetchingCatalogue, setIsFetchingCatalogue] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const countRef = useRef(0);

  const hasProductCatalogueFeature = useIsFeatureInTier('PRODUCT_CATALOGUE');

  const isLoading = isCreatingCatalogue || isFetchingCatalogue;

  useEffect(() => {
    if (!isCreatingCatalogue && createCatalogueSuccess) {
      setIsFetchingCatalogue(true);
    }
  }, [createCatalogueSuccess, isCreatingCatalogue]);

  useEffect(() => {
    if (!isFetchingCatalogue || !hasProductCatalogueFeature) {
      return;
    }

    setIsFetchingCatalogue(true);
    const id = setInterval(() => {
      fetchProductsCatalogue();
      countRef.current += 1;
    }, INTERVAL);

    if (countRef.current > LIMIT_TRIES) {
      clearInterval(id);
      setIsFetchingCatalogue(false);
      setError(true);
      setSuccess(false);
      onError && onError();
      countRef.current = 0;
    }

    if (catalogue && catalogue.state === 'FAILED') {
      clearInterval(id);
      setIsFetchingCatalogue(false);
      setError(true);
      setSuccess(false);
      onError && onError();
      countRef.current = 0;
    }

    if (catalogue && (!!catalogue.fileId || catalogue.state === 'BUILT')) {
      clearInterval(id);
      setIsFetchingCatalogue(false);
      countRef.current = 0;
      setSuccess(true);
      setError(false);
      onSuccess && onSuccess();
    }
    return () => {
      clearInterval(id);
      setIsFetchingCatalogue(false);
    };
  }, [
    catalogue,
    fetchProductsCatalogue,
    hasProductCatalogueFeature,
    isFetchingCatalogue,
    onError,
    onSuccess,
  ]);

  const downloadCatalogue = () => {
    if (!catalogue || !catalogue.fileId) {
      setError(true);
    }
    const url = getImageUrl(catalogue.fileId);
    window.open(url, '_blank');
  };

  return {
    createCatalogue: createProductsCatalogue,
    isLoading,
    catalogue,
    downloadCatalogue,
    error,
    success,
  };
}

export function useProductMetadata() {
  const [productMetadata, setProductMetadata] = useState<
    ProductMetaDataSetting | typeof undefined
  >();
  const [loading, setLoading] = useState(false);

  const getProductMetadata = useCallback((productId: string) => {
    setLoading(true);
    getProductMetadataSettingService(productId)
      .then(productMetadata => {
        setProductMetadata(productMetadata);
      })
      .catch(error => {
        setProductMetadata(undefined);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const editProductMetadata = (
    productId: string,
    metadata: ProductMetadataSettingRequest
  ) => {
    setLoading(true);
    return editProductService(productId, {
      seoSetting: metadata,
    })
      .then(() => {
        getProductMetadata(productId);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return {
    loading,
    productMetadata,
    getProductMetadata,
    editProductMetadata,
  };
}

const specificationsSelector = (state: RootState) =>
  state.products.productSpecification;

export function useProductSpecifications() {
  const fetchSpecificationsAction = useActions(
    ProductsActions.fetchProductSpecifications
  );

  const specificationSetting = useSelector(specificationsSelector);

  const fetchSpecifications = useCallback(
    (productId: string) => {
      fetchSpecificationsAction({productId});
    },
    [fetchSpecificationsAction]
  );

  return {
    fetchSpecifications,
    specificationSetting,
  };
}

export function useResetSpecifications() {
  const resetSpecificationAction = useActions(
    ProductsActions.resetSpecificationSettings
  );

  return resetSpecificationAction;
}

const relatedProductsSelector = (state: RootState) =>
  state.products.relatedProducts;

export function useRelatedProducts() {
  const relatedProducts = useSelector(relatedProductsSelector);

  const fetchRelatedProductsAction = useActions(
    ProductsActions.fetchRelatedProducts
  );

  const fetchRelatedProducts = useCallback(
    (productId: string) => {
      fetchRelatedProductsAction({productId});
    },
    [fetchRelatedProductsAction]
  );

  return {
    fetchRelatedProducts,
    relatedProducts,
  };
}

export function useResetRelatedProducts() {
  const resetRelatedProductsAction = useActions(
    ProductsActions.resetRelatedProducts
  );

  return resetRelatedProductsAction;
}

type UseSearchProductsOptions = {
  initialList?: boolean,
};
export function useSearchMinimalProducts({
  initialList,
}: UseSearchProductsOptions = {}) {
  const [result, setResult] = useState<MinimalProduct[]>([]);
  const [loading, setLoading] = useState(false);

  const searchProductsByName = useCallback((search: string) => {
    setLoading(true);
    searchProductsMinimalService(search, {
      size: 20,
    })
      .then(({data}) => {
        setResult(data);
      })
      .catch(error => {
        setResult([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (initialList) {
      getProductsMinimalService({
        size: 20,
      })
        .then(({data}) => {
          setResult(data);
        })
        .catch(error => {
          setResult([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [initialList, searchProductsByName]);

  return {products: result, loading, searchProductsByName};
}

export function useMinimalProducts(
  params: ProductFilterParams
): {
  products: MinimalProduct[],
  loading: boolean,
} {
  const [products, setProducts] = useState<MinimalProduct[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getProductsMinimalService(params)
      .then(({data}) => {
        setProducts(data);
      })
      .catch(error => {
        setProducts([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [params]);

  return {products, loading};
}

const isSavingProductVariantSelector = (state: RootState) =>
  state.products.isSaving;

export function useProductVariantActions() {
  const editProductVariantAction = useActions(
    ProductsActions.editProductVariant
  );

  const loading = useSelector(isSavingProductVariantSelector);

  const setVariantStock = useCallback(
    (productId: string, variantId: string, stock: number) => {
      editProductVariantAction({
        editProductVariantRequest: {
          productId,
          variantId,
          request: {
            stock,
          },
        },
      });
    },
    [editProductVariantAction]
  );

  return {
    setVariantStock,
    loading,
  };
}

const featuredProductsSelector = (state: RootState) =>
  state.products.featuredProducts;

export function useFeaturedProducts() {
  const featuredProducts = useSelector(featuredProductsSelector);
  const fetchFeaturedProducts = useActions(
    ProductsActions.fetchFeaturedProducts
  );
  useEffect(() => {
    if (!featuredProducts) {
      fetchFeaturedProducts();
    }
  }, [featuredProducts, fetchFeaturedProducts]);

  return featuredProducts;
}

export function useListProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchProducts = useCallback((params?: ListProductsParams) => {
    setLoading(true);
    return listProductsService(params)
      .then(({data}) => {
        setProducts(data);
        return data;
      })
      .catch(() => {
        setProducts([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return {
    products,
    fetchProducts,
    loading,
  };
}

export function useProductTax() {
  const [loading, setLoading] = useState(false);

  const setProductTax = (taxId: string, productId: string) => {
    setLoading(true);

    return setProductTaxService(productId, taxId)
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        throw new Error('Error setting product tax');
      });
  };

  return {
    setProductTax,
    loading,
  };
}
